import React, { FC, memo, ReactNode, useEffect, useRef, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { Theme } from '../../types';

const DIRTY_COLOR = '#ed6c02';
const ERROR_COLOR = '#d32f2f';
const SAVED_COLOR = '#2e7d32';

const useStyles = makeStyles((theme: Theme) => ({
    section: {
        marginTop: theme.spacing(2.5),
        '&:first-of-type': {
            marginTop: theme.spacing(0.5)
        }
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        padding: theme.spacing(0.5, 0),
        paddingLeft: theme.spacing(0.75),
        userSelect: 'none',
        borderRadius: 4,
        borderLeft: '3px solid transparent',
        transition: 'border-color 0.3s ease, background-color 0.2s ease',
        '&:hover $chevron': {
            color: theme.palette.primary.main
        },
        '&:hover $label': {
            color: theme.palette.primary.main
        }
    },
    headerDirty: {
        borderLeftColor: DIRTY_COLOR
    },
    headerError: {
        borderLeftColor: ERROR_COLOR
    },
    headerSaved: {
        borderLeftColor: SAVED_COLOR,
        animation: '$fadeOutBorder 2s ease forwards'
    },
    '@keyframes fadeOutBorder': {
        '0%': { borderLeftColor: SAVED_COLOR },
        '70%': { borderLeftColor: SAVED_COLOR },
        '100%': { borderLeftColor: 'transparent' }
    },
    chevron: {
        transition: 'transform 0.2s ease',
        fontSize: '1.1rem',
        color: theme.palette.grey[400],
        marginRight: theme.spacing(0.75)
    },
    chevronOpen: {
        transform: 'rotate(90deg)',
        color: theme.palette.primary.main
    },
    label: {
        fontWeight: 600,
        fontSize: '13px',
        // Brighter section headers in dark mode (text.secondary reads too faint).
        color: theme.palette.mode === 'dark' ? 'var(--input-label)' : theme.palette.text.secondary,
        letterSpacing: '0.02em',
        textTransform: 'uppercase' as const,
        transition: 'color 0.15s ease'
    },
    requiredAsterisk: {
        color: ERROR_COLOR,
        marginLeft: 2,
        fontWeight: 700
    },
    dirtyAsterisk: {
        color: DIRTY_COLOR,
        marginLeft: 2,
        fontWeight: 700
    },
    separator: {
        flex: 1,
        height: 1,
        backgroundColor: theme.palette.grey[200],
        marginLeft: theme.spacing(1.5)
    },
    content: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1)
    },
    contentCard: {
        padding: theme.spacing(2, 2.5),
        // In dark mode keep the section fill transparent (border-only) so it
        // doesn't add a muddy grey layer — the dialog surface shows through and
        // the (lighter, bordered) inputs stand out clearly against it.
        backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : theme.palette.grey[50],
        borderRadius: 8,
        border: `1px solid ${
            theme.palette.mode === 'dark' ? 'var(--input-border)' : theme.palette.grey[100]
        }`
    }
}));

interface Props {
    label: string;
    defaultOpen?: boolean;
    children: ReactNode;
    /** When true, renders compact style for right-side panel */
    compact?: boolean;
    /** When true, content gets a subtle card-like background */
    card?: boolean;
    /** Controlled open state — overrides internal state when provided */
    open?: boolean;
    /** Optional style for the outer wrapper div */
    style?: React.CSSProperties;
    /** Optional content rendered at the far right of the header line */
    rightContent?: ReactNode;
    /** Optional content rendered before the label (e.g. a checkbox) */
    leftContent?: ReactNode;
    /** When true, shows a red asterisk next to the label */
    required?: boolean;
    /** Orange left border + orange asterisk — indicates unsaved changes */
    isDirty?: boolean;
    /** Newly added — auto-expands on mount and shows orange asterisk */
    isNew?: boolean;
    /** Red left border + red asterisk — auto-expands when errors appear */
    hasErrors?: boolean;
    /** Trigger a one-shot green fade-out animation (e.g. on save) */
    showSavedFlash?: boolean;
}

const CollapsibleSection: FC<Props> = ({
    label,
    defaultOpen = true,
    compact = false,
    card = false,
    open,
    children,
    style,
    rightContent,
    leftContent,
    required = false,
    isDirty = false,
    isNew = false,
    hasErrors = false,
    showSavedFlash = false
}) => {
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(isNew ? true : defaultOpen);

    useEffect(() => {
        if (open !== undefined) setIsOpen(open);
    }, [open]);

    // Auto-expand when errors appear in a closed section
    const prevHasErrors = useRef(hasErrors);
    useEffect(() => {
        if (hasErrors && !prevHasErrors.current && !isOpen) {
            setIsOpen(true);
        }
        prevHasErrors.current = hasErrors;
    }, [hasErrors, isOpen]);

    // Re-trigger green flash whenever showSavedFlash flips true
    const [flashKey, setFlashKey] = useState(0);
    const prevFlash = useRef(showSavedFlash);
    useEffect(() => {
        if (showSavedFlash && !prevFlash.current) {
            setFlashKey((k) => k + 1);
        }
        prevFlash.current = showSavedFlash;
    }, [showSavedFlash]);

    const fillHeight = !!style?.height;

    const headerStateClass = hasErrors
        ? classes.headerError
        : (isDirty || isNew)
            ? classes.headerDirty
            : '';
    const showDirtyAsterisk = (isDirty || isNew) && !hasErrors && !required;

    return (
        <div className={classes.section} style={fillHeight ? { ...style, display: 'flex', flexDirection: 'column' } : style}>
            <div
                key={flashKey}
                className={`${classes.header} ${headerStateClass} ${flashKey > 0 ? classes.headerSaved : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <ChevronRightIcon
                    className={`${classes.chevron} ${isOpen ? classes.chevronOpen : ''}`}
                />
                {leftContent}
                <Typography className={classes.label}>
                    {label}
                    {(required || hasErrors) && <span className={classes.requiredAsterisk}>*</span>}
                    {showDirtyAsterisk && <span className={classes.dirtyAsterisk}>*</span>}
                </Typography>
                {!compact && <div className={classes.separator} />}
                {rightContent}
            </div>
            {isOpen && (
                <div
                    className={card ? classes.contentCard : classes.content}
                    style={fillHeight ? { flex: 1, minHeight: 0 } : undefined}
                >
                    {children}
                </div>
            )}
        </div>
    );
};

export default memo(CollapsibleSection);
